'use client';
/**
 * DOSSIERS RH — la liste du personnel, chacun avec son dossier (RQ, 04/10/2026).
 *
 * Le dossier des AUTRES s'ouvrait depuis « Mon dossier RH », dans le menu du
 * profil : un sélecteur y proposait tout le personnel au gestionnaire. La RQ ne
 * veut plus y passer — le profil, c'est « mon espace » ; la gestion du
 * personnel est une rubrique de la barre, comme le planning ou les absences.
 *
 * Ici : la liste, une recherche, et la fiche COMPLÈTE de la personne
 * (`DossierPersonnel`, la même que sur l'organigramme — un seul écran de fiche,
 * sinon deux finissent par diverger). Visible seulement à qui voit TOUS les
 * dossiers ET porte le rôle `rh` — « Dossier RH est uniquement pour le RH » ; chacun
 * garde « Mon dossier RH » pour le sien.
 */
import React from 'react';
import { Bouton } from '@/shared/Bouton';
import { useGed } from '@/lib/store';
import { ADRESSES } from '@/lib/adresse';
import { TitrePage, EmptyState } from '@/components/ui';
import { Tableau, ligneOuvrable } from '@/shared/Tableau';
import type { RhPersonnel } from '@/lib/types';
import { DossierPersonnel } from '@/components/screens/ressources/DossierPersonnel';
import { nomDe } from '@/lib/nom';
import { pastilleDe } from '@/lib/couleurs-personne';
import { fmtDate } from '@/lib/meta';

export function DossiersRh() {
  const { rhPersonnel, loadRh, session, rhAcces, go } = useGed();
  const [voirSortis, setVoirSortis] = React.useState(false);
  const [ouvertLocal, setOuvertLocal] = React.useState<string | null>(null);
  const [lu, setLu] = React.useState(false);

  /* ── OUVERTURE DEMANDÉE PAR UN AUTRE ÉCRAN (06/10/2026) ───────────────────
   *
   * Depuis le 06/10, l'organigramme n'ouvre qu'un résumé en lecture : c'est son
   * bouton « Ouvrir le dossier RH » qui envoie ici, et l'annuaire des comptes
   * comme le parcours d'intégration en font autant. Sans cette consommation, le
   * bouton déposait sur la liste et laissait retrouver la ligne à la main.
   *
   * ⚠️ LA DEMANDE SE LIT AU RENDU, elle ne se recopie pas par un `setState`
   * dans un effet : `react-hooks/set-state-in-effect` est une ERREUR dans ce
   * projet, et un effet qui pose l'état déclenche un rendu en cascade — le
   * dossier s'ouvrirait une image après l'arrivée. `ouvertLocal` ne porte donc
   * que les ouvertures faites DEPUIS cette liste, et il a la priorité : cliquer
   * une autre ligne doit l'emporter sur la demande qui nous a amenés ici.
   *
   * ⚠️ La clé est effacée À LA SORTIE (fermeture du dossier, ou démontage de
   * l'écran), jamais à l'arrivée : l'effacer tout de suite ferait retomber
   * `ouvert` à `null` au rendu suivant, et le dossier se refermerait seul.
   * Sans l'effacer du tout, revenir sur l'écran par la barre rouvrirait la
   * fiche de la dernière personne consultée. */
  const personneDemandee = useGed((s) => s.selectedPersonne);
  const selectionner = useGed((s) => s.select);
  const ouvert = ouvertLocal ?? personneDemandee ?? null;
  const ouvrir = (id: string | null) => {
    setOuvertLocal(id);
    if (personneDemandee) selectionner('selectedPersonne', null);
  };
  /* Au démontage, la clé n'est effacée que si l'écran COURANT ne la lit pas
     (03/11/2026). Deux défauts tenaient à l'effacement inconditionnel :
     - en développement, React (mode strict) démonte puis remonte chaque écran
       à l'arrivée : ce faux démontage effaçait la personne demandée, et
       « Ouvrir le dossier RH » (organigramme, comptes, intégration) déposait
       sur la liste au lieu du dossier — `view` vaut alors encore `dossiers` ;
     - le bouton « Parcours » du dossier pose `selectedPersonne` puis change
       d'écran : le parcours retombait sur celui de la personne connectée. */
  React.useEffect(() => () => {
    if (ADRESSES[useGed.getState().view]?.cle !== 'selectedPersonne') selectionner('selectedPersonne', null);
  }, [selectionner]);

  // La liste du personnel n'est chargée que par les écrans RH : on la demande
  // si elle est vide (arrivée directe par l'adresse).
  React.useEffect(() => {
    let vivant = true;
    if (rhPersonnel.length) { void Promise.resolve().then(() => { if (vivant) setLu(true); }); return () => { vivant = false; }; }
    void loadRh().finally(() => { if (vivant) setLu(true); });
    return () => { vivant = false; };
  }, [rhPersonnel.length, loadRh]);

  // Réservé au SEUL rôle `rh` (RQ, 04/10/2026), et à qui voit tout le personnel.
  const portee = session.sections?.dossierRh?.portee;
  if (rhAcces !== null && (!rhAcces?.acces || (portee && portee !== 'tout'))) {
    return <EmptyState titre="Rubrique réservée au gestionnaire RH." indice="Votre propre dossier est dans « Mon dossier RH », depuis le rond de votre profil." />;
  }

  /* L'ordre par défaut est alphabétique ; le tableau trie ensuite comme on veut. */
  const lignes = rhPersonnel
    .filter((p) => voirSortis || p.statut !== 'sorti')
    .sort((a, b) => nomDe(a).localeCompare(nomDe(b), 'fr'));
  const sortis = rhPersonnel.filter((p) => p.statut === 'sorti').length;
  const etatDe = (p: RhPersonnel): 'sorti' | 'externe' | 'actif' => (p.statut === 'sorti' ? 'sorti' : p.type === 'externe' ? 'externe' : 'actif');

  return (
    <section style={{ display: 'grid', gap: 14 }}>
      <TitrePage eyebrow={<>Personnel &amp; plannings</>} titre="Dossiers RH"
        sousTitre="La liste du personnel. Un clic ouvre le dossier complet de la personne : identité, contrat, intégration, compte." />
      {session.sections?.rhjournal?.voir && (
        <Bouton variante="discret" taille="sm" onClick={() => go('rhjournal')} style={{ justifySelf: 'start' }}>
          Journal RH — qui a fait quoi, et quand →
        </Bouton>
      )}

      {sortis > 0 && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--text-muted)', cursor: 'pointer', justifySelf: 'start' }}>
          <input type="checkbox" checked={voirSortis} onChange={(e) => setVoirSortis(e.target.checked)} />
          Afficher les {sortis} personne(s) sorties
        </label>
      )}

      {/* L8 (11/10/2026) : cette liste était un tableau sans en-tête — cinq
          colonnes alignées, une fiche en FENÊTRE. Elle passe sur le socle au
          niveau STANDARD : recherche (nom, fonction, badge), filtres sur la
          fonction et l'état, tri, états intégrés. La ligne ouvre le dossier. */}
      <Tableau
        id="dossiers-rh"
        ajusterLargeur
        niveau="standard"
        recherche="Rechercher (nom, fonction, badge)…"
        chargement={!lu}
        vide={{ titre: 'Aucune personne au registre du personnel.', indice: sortis > 0 && !voirSortis ? 'Les personnes sorties sont masquées — cochez la case pour les voir.' : undefined }}
        lignes={lignes}
        cleLigne={(p) => p.orgId}
        ligneProps={(p) => ligneOuvrable(() => ouvrir(p.orgId), `Ouvrir le dossier de ${nomDe(p)}`)}
        colonnes={[
          {
            cle: 'nom', libelle: 'Personne', valeur: (p) => nomDe(p), obligatoire: true,
            rendu: (p) => {
              const pastille = pastilleDe(p.orgId, p.couleur);
              return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 28, height: 28, padding: '0 4px', borderRadius: 'var(--radius-pill)', background: pastille.background, color: pastille.color, border: `1px solid ${pastille.borderColor}`, fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700 }}>
                    {p.initiales || nomDe(p).split(/\s+/).slice(0, 2).map((m) => m[0]).join('').toUpperCase()}
                  </span>
                  <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{nomDe(p)}</span>
                </span>
              );
            },
          },
          { cle: 'fonction', libelle: 'Fonction', type: 'liste', valeur: (p) => p.fonction || '', style: { color: 'var(--text-muted)' }, rendu: (p) => p.fonction || '—' },
          { cle: 'badge', libelle: 'Badge', valeur: (p) => p.badgeId || '', style: { fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }, rendu: (p) => p.badgeId || '—' },
          { cle: 'entree', libelle: 'Entrée', type: 'date', valeur: (p) => p.dateEntree || '', style: { color: 'var(--text-muted)', whiteSpace: 'nowrap' }, rendu: (p) => fmtDate(p.dateEntree) },
          {
            cle: 'etat', libelle: 'État', type: 'liste', valeur: (p) => etatDe(p),
            rendu: (p) => (
              <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', whiteSpace: 'nowrap',
                background: p.statut === 'sorti' ? 'var(--gray-50)' : 'var(--green-50)', color: p.statut === 'sorti' ? 'var(--text-muted)' : 'var(--green-600)' }}>
                {etatDe(p)}
              </span>
            ),
          },
        ]}
      />

      {ouvert && (
        <DossierPersonnel
          key={ouvert}
          orgId={ouvert}
          onClose={() => ouvrir(null)}
        />
      )}
    </section>
  );
}
